/**
 * ProgressRing - Circular progress indicator
 *
 * Shows phase completion percentage as an SVG ring with:
 * - Color-coded progress (red < 30%, amber 30-70%, green > 70%)
 * - Percentage label in center
 * - Smooth animation
 */

// ============================================================================
// TYPES
// ============================================================================

interface ProgressRingProps {
  progress: number // 0-100
  size?: number // Diameter in pixels
  strokeWidth?: number // Ring thickness
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ProgressRing({ progress, size = 48, strokeWidth = 4 }: ProgressRingProps) {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (progress / 100) * circumference

  // Color-coded by progress
  const progressColor =
    progress >= 70 ? 'var(--color-success)' :
    progress >= 30 ? 'var(--color-warning)' :
    'var(--color-error)'

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      {/* Background Ring */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--color-warm-200)"
        strokeWidth={strokeWidth}
      />

      {/* Progress Ring */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={progressColor}
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-300 ease-out"
      />

      {/* Percentage Text */}
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="central"
        className="text-xs font-semibold fill-warm-700 transform rotate-90"
        style={{ transformOrigin: 'center' }}
      >
        {Math.round(progress)}%
      </text>
    </svg>
  )
}
